import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Edit, Save, Plus, Trash2, X, Image } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CageDetails = ({ cageNumber, animals, getAuthHeaders, onClose, onRefresh }) => {
  const [subRooms, setSubRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomData, setRoomData] = useState({
    capacity: 10,
    temperature: "",
    humidity: "",
    notes: ""
  });
  const [animalDialogOpen, setAnimalDialogOpen] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [selectedSubRoom, setSelectedSubRoom] = useState(null);
  const [animalFormData, setAnimalFormData] = useState({
    animal_type: "rabbit",
    breed: "",
    age: "",
    gender: "male",
    purchase_date: "",
    birth_date: "",
    weight: "",
    health_status: "healthy",
    cage_number: cageNumber,
    sub_room: "",
    photo: "",
    group: "babies"
  });
  const animalPhotoRef = React.useRef(null);

  useEffect(() => {
    fetchSubRooms();
  }, [cageNumber]);

  const fetchSubRooms = async () => {
    try {
      const response = await axios.get(`${API}/sub-rooms?cage_number=${cageNumber}`, { headers: getAuthHeaders() });
      if (response.data.length === 0) {
        await initializeSubRooms();
      } else {
        setSubRooms(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sub-rooms:", error);
      setLoading(false);
    }
  };

  const initializeSubRooms = async () => {
    try {
      await axios.post(`${API}/sub-rooms/initialize/${cageNumber}`, {}, { headers: getAuthHeaders() });
      const response = await axios.get(`${API}/sub-rooms?cage_number=${cageNumber}`, { headers: getAuthHeaders() });
      setSubRooms(response.data);
      toast.success("Sub-rooms initialized");
    } catch (error) {
      toast.error("Failed to initialize sub-rooms");
    }
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room.room_label);
    setRoomData({
      capacity: room.capacity || 10,
      temperature: room.temperature || "",
      humidity: room.humidity || "",
      notes: room.notes || ""
    });
  };

  const handleSaveRoom = async () => {
    try {
      await axios.put(
        `${API}/sub-rooms/${cageNumber}/${editingRoom}`,
        roomData,
        { headers: getAuthHeaders() }
      );
      toast.success("Sub-room updated");
      setEditingRoom(null);
      fetchSubRooms();
    } catch (error) {
      toast.error("Failed to update sub-room");
    }
  };

  const getAnimalsInRoom = (roomLabel) => {
    return animals.filter(a => a.sub_room === roomLabel && a.cage_number === cageNumber);
  };

  const handleAddAnimalToRoom = (roomLabel) => {
    setSelectedSubRoom(roomLabel);
    setEditingAnimal(null);
    setAnimalFormData({
      animal_type: "rabbit",
      breed: "",
      age: "",
      gender: "male",
      purchase_date: "",
      birth_date: "",
      weight: "",
      health_status: "healthy",
      cage_number: cageNumber,
      sub_room: roomLabel,
      photo: "",
      group: "babies"
    });
    setAnimalDialogOpen(true);
  };

  const handleEditAnimal = (animal) => {
    setEditingAnimal(animal);
    setAnimalFormData({
      animal_type: animal.animal_type,
      breed: animal.breed,
      age: animal.age,
      gender: animal.gender || "male",
      purchase_date: animal.purchase_date || "",
      birth_date: animal.birth_date || "",
      weight: animal.weight,
      health_status: animal.health_status,
      cage_number: animal.cage_number,
      sub_room: animal.sub_room || "",
      photo: animal.photo || "",
      group: animal.group
    });
    setAnimalDialogOpen(true);
  };

  const handleDeleteAnimal = async (animalId) => {
    if (window.confirm("Are you sure you want to delete this animal?")) {
      try {
        await axios.delete(`${API}/animals/${animalId}`, { headers: getAuthHeaders() });
        toast.success("Animal deleted successfully");
        onRefresh();
        fetchSubRooms();
      } catch (error) {
        toast.error("Failed to delete animal");
      }
    }
  };

  const handleAnimalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAnimal) {
        await axios.put(`${API}/animals/${editingAnimal.id}`, animalFormData, { headers: getAuthHeaders() });
        toast.success("Animal updated successfully");
      } else {
        await axios.post(`${API}/animals`, animalFormData, { headers: getAuthHeaders() });
        toast.success("Animal added successfully");
      }
      setAnimalDialogOpen(false);
      onRefresh();
      fetchSubRooms();
    } catch (error) {
      toast.error("Failed to save animal");
    }
  };

  const handleAnimalPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setAnimalFormData({...animalFormData, photo: reader.result});
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <div className="p-4">Loading sub-rooms...</div>;
  }

  return (
    <div className="cage-details">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Cage {cageNumber} - Sub-Rooms</h2>
        <Button onClick={onClose} variant="outline">Close</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {subRooms.map(room => {
          const roomAnimals = getAnimalsInRoom(room.room_label);
          return (
            <Card key={room.id} className={`sub-room-card ${roomAnimals.length > 0 ? 'occupied' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Room {room.room_label}</CardTitle>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditRoom(room)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
                <CardDescription>{roomAnimals.length}/{room.capacity} animals</CardDescription>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {room.temperature && <div><strong>Temp:</strong> {room.temperature}</div>}
                {room.humidity && <div><strong>Humidity:</strong> {room.humidity}</div>}
                {room.notes && <div className="text-xs text-gray-600 mt-2">{room.notes}</div>}
                
                {roomAnimals.length > 0 && (
                  <div className="mt-2 pt-2 border-t space-y-2">
                    <strong className="block">Animals:</strong>
                    {roomAnimals.map(a => (
                      <div key={a.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="flex-1 cursor-pointer" onClick={() => handleEditAnimal(a)}>
                          <div className="text-xs font-semibold">{a.breed}</div>
                          <div className="text-xs text-gray-600">{a.animal_type.replace('_', ' ')} • {a.gender}</div>
                          <Badge className={`text-xs ${
                            a.health_status === 'healthy' ? 'bg-green-500' : 
                            a.health_status === 'sick' ? 'bg-red-500' : 'bg-yellow-500'
                          }`}>
                            {a.health_status}
                          </Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditAnimal(a);
                            }}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAnimal(a.id);
                            }}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <Button
                  size="sm"
                  className="w-full mt-2"
                  variant="outline"
                  onClick={() => handleAddAnimalToRoom(room.room_label)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Animal
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Room Dialog */}
      <Dialog open={editingRoom !== null} onOpenChange={() => setEditingRoom(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Room {editingRoom}</DialogTitle>
            <DialogDescription>Update room details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Capacity</Label>
              <Input
                type="number"
                value={roomData.capacity}
                onChange={(e) => setRoomData({...roomData, capacity: parseInt(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label>Temperature</Label>
              <Input
                value={roomData.temperature}
                onChange={(e) => setRoomData({...roomData, temperature: e.target.value})}
                placeholder="e.g., 22°C"
              />
            </div>
            <div className="space-y-2">
              <Label>Humidity</Label>
              <Input
                value={roomData.humidity}
                onChange={(e) => setRoomData({...roomData, humidity: e.target.value})}
                placeholder="e.g., 60%"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={roomData.notes}
                onChange={(e) => setRoomData({...roomData, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingRoom(null)}>Cancel</Button>
              <Button onClick={handleSaveRoom}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Animal Dialog */}
      <Dialog open={animalDialogOpen} onOpenChange={setAnimalDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAnimal ? "Edit Animal" : "Add New Animal"}</DialogTitle>
            <DialogDescription>
              {editingAnimal ? "Update animal details" : `Add animal to Cage ${cageNumber} - Room ${selectedSubRoom}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAnimalSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="animal_type">Animal Type</Label>
                <Select 
                  value={animalFormData.animal_type} 
                  onValueChange={(value) => setAnimalFormData({...animalFormData, animal_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rabbit">Rabbit</SelectItem>
                    <SelectItem value="grass_cutter">Grass Cutter</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="breed">Breed</Label>
                <Input
                  value={animalFormData.breed}
                  onChange={(e) => setAnimalFormData({...animalFormData, breed: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="age">Age (months)</Label>
                <Input
                  type="number"
                  value={animalFormData.age}
                  onChange={(e) => setAnimalFormData({...animalFormData, age: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select 
                  value={animalFormData.gender} 
                  onValueChange={(value) => setAnimalFormData({...animalFormData, gender: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={animalFormData.weight}
                  onChange={(e) => setAnimalFormData({...animalFormData, weight: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="purchase_date">Purchase Date</Label>
                <Input
                  type="date"
                  value={animalFormData.purchase_date}
                  onChange={(e) => setAnimalFormData({...animalFormData, purchase_date: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Birth Date</Label>
                <Input
                  type="date"
                  value={animalFormData.birth_date}
                  onChange={(e) => setAnimalFormData({...animalFormData, birth_date: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="health_status">Health Status</Label>
                <Select 
                  value={animalFormData.health_status} 
                  onValueChange={(value) => setAnimalFormData({...animalFormData, health_status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="healthy">Healthy</SelectItem>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="under_treatment">Under Treatment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cage_number">Cage Number</Label>
                <Input
                  value={animalFormData.cage_number}
                  onChange={(e) => setAnimalFormData({...animalFormData, cage_number: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sub_room">Sub-Room</Label>
                <Select 
                  value={animalFormData.sub_room} 
                  onValueChange={(value) => setAnimalFormData({...animalFormData, sub_room: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sub-room" />
                  </SelectTrigger>
                  <SelectContent>
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'].map(label => (
                      <SelectItem key={label} value={label}>Room {label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="photo">Animal Photo</Label>
                <input
                  type="file"
                  ref={animalPhotoRef}
                  accept="image/*"
                  className="hidden"
                  onChange={handleAnimalPhotoSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => animalPhotoRef.current?.click()}
                  className="w-full"
                >
                  <Image className="w-4 h-4 mr-2" />
                  {animalFormData.photo ? "Change Photo" : "Upload Photo"}
                </Button>
                {animalFormData.photo && (
                  <div className="animal-photo-container mt-2">
                    <img
                      src={animalFormData.photo}
                      alt="Animal preview"
                      className="animal-photo-preview"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="animal-photo-remove"
                      onClick={() => setAnimalFormData({...animalFormData, photo: ""})}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="group">Group</Label>
                <Select 
                  value={animalFormData.group} 
                  onValueChange={(value) => setAnimalFormData({...animalFormData, group: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stock">Stock</SelectItem>
                    <SelectItem value="babies">Babies</SelectItem>
                    <SelectItem value="mothers">Mothers</SelectItem>
                    <SelectItem value="crossed">Crossed</SelectItem>
                    <SelectItem value="pregnant">Pregnant</SelectItem>
                    <SelectItem value="weaning">Weaning</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="ready_to_sell">Ready to Sell</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setAnimalDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingAnimal ? "Update" : "Add"} Animal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CageDetails;
